import { FeatureToggles } from '@/features/feature-toggles'
import { action, computed } from 'mobx'
import { type Checklist } from '@/features/jobs/info-gathering/Checklist'
import { type Job } from '@/features/jobs/Job'
import { type NotesDialogViewModel } from '@/features/jobs/details/components/CapturedInformationCard/NotesDialog/NotesDialogViewModel'
import { type IsFirmAdminInWorkspaceFn } from '@/features/jobs/details/JobRootViewModel'

/**
 * The notes tab of the captured information: the client's notes, or the
 * instructions once info gathering is done.
 */
export class NotesTabViewModel {
  constructor(
    private readonly _job: Job,
    readonly notesDialogVm: NotesDialogViewModel,
    private readonly _getChecklist: () => Checklist | undefined,
    private readonly _isFirmAdminInWorkspace: IsFirmAdminInWorkspaceFn,
  ) {}

  /**
   * Determine that we are gathering that info for the job.
   */
  @computed
  get isInfoGatheringStep() {
    return this._job.status === 'InfoGathering'
  }

  /**
   * Whether the current user may edit the notes: firm admins, during info gathering.
   */
  @computed
  get canEditNotes() {
    return this._isFirmAdmin && this.isInfoGatheringStep
  }

  /**
   * The checklist item that holds the notes.
   */
  @computed
  get notesSupportingItem() {
    return this._notesSection?.notesSupportingItem
  }

  /**
   * The notes entered so far, if any.
   */
  @computed
  get notesSupportingItemInput() {
    return this.notesSupportingItem?.input ?? null
  }

  /**
   * Whether the notes feature is on and the checklist has a notes item.
   */
  @computed
  get areNotesEnabled() {
    return Boolean(
      FeatureToggles.notesSupporting() && !!this.notesSupportingItem,
    )
  }

  /**
   * Opens the notes dialog with the current notes.
   */
  @action.bound
  showNotesDialog() {
    const item = this.notesSupportingItem
    if (item) {
      this.notesDialogVm.show(item.id, item.input?.text ?? '')
    }
  }

  @computed
  private get _isFirmAdmin() {
    return this._isFirmAdminInWorkspace(this._job.workspace.id)
  }

  @computed
  private get _notesSection() {
    return this._getChecklist()?.notesSection
  }
}
